import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Navbar from './Navbar';
import Footer from './Footer';
import Login from './Login';
import { v4 as uuidv4 } from 'uuid';

import {
  FaClock, FaCog, FaTruck, FaCheckCircle, FaTimesCircle,
  FaBoxOpen, FaReceipt, FaUser, FaEnvelope, FaPhone,
  FaMapMarkerAlt, FaTimes, FaArrowLeft, FaStar,
  FaUndo, FaExclamationTriangle, FaBox, FaWeight, FaFlask,
  FaCreditCard, FaLayerGroup, FaShoppingCart, FaHeart, FaRegHeart,
  FaShareAlt
} from 'react-icons/fa';
import { Icon } from '@iconify/react';

const BASE = 'https://evercorenutrition.com';

const getImage = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BASE}${url}`;
};

// ===========================================
// GUEST CART FUNCTIONS - SAME AS Shop.jsx
// ===========================================
const GUEST_CART_KEY = 'guest_cart';

const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error getting guest cart:', error);
    return [];
  }
};

const saveGuestCart = (cartItems) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error('Error saving guest cart:', error);
  }
};

const addComboToGuestCart = (comboId, quantity, comboDetails) => {
  const guestCart = getGuestCart();
  const existingItemIndex = guestCart.findIndex(
    item => item.combo_id === comboId && item.item_type === 'combo'
  );
  
  if (existingItemIndex !== -1) {
    guestCart[existingItemIndex].quantity += quantity;
    // console.log('Updated existing combo in guest cart:', guestCart[existingItemIndex]);
  } else {
    guestCart.push({
      combo_id: comboId,
      quantity: quantity,
      name: comboDetails.name,
      unit_price: parseFloat(comboDetails.selling_price),
      price: parseFloat(comboDetails.selling_price),
      image: comboDetails.image_url,
      added_at: new Date().toISOString(),
      item_type: 'combo'
    });
    // console.log('Added new combo to guest cart:', { comboId, quantity, comboDetails });
  }
  
  saveGuestCart(guestCart);
  // console.log('Guest cart after adding combo:', getGuestCart());
  return guestCart;
};

const mergeGuestCartWithUserCart = async (customerId, token) => {
  const guestCart = getGuestCart();
  
  if (!guestCart || guestCart.length === 0) {
    return { success: true, message: 'No guest cart to merge' };
  }
  
  // console.log('Merging guest cart:', guestCart);
  let mergedCount = 0;
  
  try {
    for (const item of guestCart) {
      let body = {};
      
      if (item.item_type === 'combo') {
        body = {
          customer_id: String(customerId),
          combo_id: item.combo_id,
          quantity: item.quantity
        };
        // console.log('Merging combo:', body);
      } else {
        body = {
          customer_id: String(customerId),
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity
        };
        // console.log('Merging product:', body);
      }
      
      const response = await fetch(`${BASE}/ev_admin/api/cart/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        mergedCount++;
      }
    }
    
    // Clear guest cart after successful merge
    localStorage.removeItem(GUEST_CART_KEY);
    // console.log(`Merged ${mergedCount} items from guest cart`);
    
    return { success: true, message: `Merged ${mergedCount} items from guest cart` };
  } catch (error) {
    console.error('Error merging guest cart:', error);
    return { success: false, message: 'Failed to merge guest cart' };
  }
};

const sendPurchaseEvent = async (value) => {
  const eventId = uuidv4();

  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: value,
      currency: 'INR'
    }, {
      eventID: eventId
    });
  }

  await fetch('https://evercorenutrition.com/ev_admin/api/fb-event/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      event_id: eventId,
      value: value
    })
  });
};

const ComboDetail = () => {
  const { comboId } = useParams();
  const navigate = useNavigate();

  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const isAuthenticated = () => !!localStorage.getItem('authToken');

  const getCustomerId = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.customer_id || null;
      }
    } catch (e) {}
    return null;
  };

  const showWarningMessage = (message) => {
    setWarningMessage(message);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
  };

  // Handle login success - with guest cart merge
  const handleLoginSuccess = useCallback(async (user) => {
    setShowLoginPopup(false);
    
    // Merge guest cart with user cart
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();
    
    if (token && customerId) {
      const mergeResult = await mergeGuestCartWithUserCart(customerId, token);
      if (mergeResult.success && mergeResult.message !== 'No guest cart to merge') {
        showWarningMessage(mergeResult.message);
      }
    }
    
    // Refresh wishlist after login
    await fetchWishlist();
    window.dispatchEvent(new Event('cartUpdated'));
    showWarningMessage('Login successful!');
  }, []);

  const fetchComboDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try to fetch combo details directly with the comboId
      let response = await fetch(`${BASE}/ev_admin/api/combos/${comboId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // If direct fetch fails, try to find combo by numeric ID from list
      if (!response.ok && !isNaN(comboId)) {
        const listResponse = await fetch(`${BASE}/ev_admin/api/combos/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (listResponse.ok) {
          const listData = await listResponse.json();
          let combosList = listData.results || listData || [];
          
          const foundCombo = combosList.find(c => c.id === parseInt(comboId));
          if (foundCombo && foundCombo.combo_id) {
            response = await fetch(`${BASE}/ev_admin/api/combos/${foundCombo.combo_id}/`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            });
          }
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.combo) {
        setCombo(data.combo);
       
        if (data.combo.items && data.combo.items.length > 0) {
  setSelectedImage(data.combo.items[0].variant_image || data.combo.image_url);
} else {
  setSelectedImage(data.combo.image_url);
}
      } else {
        throw new Error('Invalid combo data');
      }
    } catch (err) {
      console.error('Error fetching combo:', err);
      setError(err.message || 'Failed to load combo details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (comboId) {
      fetchComboDetail();
    }
  }, [comboId]);

  useEffect(() => {
    if (isAuthenticated()) {
      fetchWishlist();
    }
  }, []);

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();
      if (!token || !customerId) return;

      const response = await fetch(
        `${BASE}/ev_admin/api/wishlist/?customer_id=${customerId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const combos = data?.wishlist?.combos || [];
        const comboIds = combos.map(item => item.id);
        setWishlist(comboIds);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  const toggleWishlist = async () => {
    if (!isAuthenticated()) {
      showWarningMessage('Please login to add to wishlist');
      setTimeout(() => {
        setShowLoginPopup(true);
      }, 1000);
      return;
    }

    if (!combo) return;

    setWishlistLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();

      if (wishlist.includes(combo.id)) {
        const response = await fetch(
          `${BASE}/ev_admin/api/wishlist/?customer_id=${customerId}&combo_id=${combo.id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          setWishlist(prev => prev.filter(id => id !== combo.id));
          showWarningMessage('Combo removed from wishlist');
        } else {
          showWarningMessage('Failed to remove from wishlist');
        }
      } else {
        const response = await fetch(`${BASE}/ev_admin/api/wishlist/`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: String(customerId),
            combo_id: combo.id,
            quantity: 1,
          }),
        });

        if (response.ok) {
          setWishlist(prev => [...prev, combo.id]);
          showWarningMessage('Combo added to wishlist!');
        } else {
          showWarningMessage('Failed to add to wishlist');
        }
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showWarningMessage('Network error. Please try again.');
    } finally {
      setWishlistLoading(false);
    }
  };

  // Updated addToCart with guest cart support
  const addToCart = async () => {
    if (!combo) return;
    
    // Prevent double click
    if (addingToCart) return;
    
    setAddingToCart(true);


    
    try {


      await sendPurchaseEvent(parseFloat(combo.selling_price) * quantity);
      // If not authenticated, save to guest cart
      if (!isAuthenticated()) {
        // console.log('User not authenticated, saving combo to guest cart');
        const comboDetailsForCart = {
          name: combo.name,
          selling_price: parseFloat(combo.selling_price),
          image_url: combo.image_url
        };
        
        addComboToGuestCart(combo.id, quantity, comboDetailsForCart);
        showWarningMessage('Combo added to cart!');
        window.dispatchEvent(new Event('cartUpdated'));
        setAddingToCart(false);
        return;
      }

      // User is authenticated, add to server cart
      // console.log('User authenticated, adding combo to server cart');
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();

      if (!customerId) {
        showWarningMessage('Could not identify customer. Please login again.');
        setAddingToCart(false);
        return;
      }

      const response = await fetch(`${BASE}/ev_admin/api/cart/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          combo_id: combo.id,
          quantity: quantity,
        }),
      });

      if (response.ok) {
        showWarningMessage('Combo added to cart!');
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        showWarningMessage(errorData.message || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showWarningMessage('Network error. Please try again.');
    } finally {
      setTimeout(() => {
        setAddingToCart(false);
      }, 500);
    }
  };

  const shareCombo = () => {
    if (!combo) return;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: combo.name, text: 'Check out this combo!', url });
    } else {
      navigator.clipboard.writeText(url);
      showWarningMessage('Combo link copied!');
    }
  };

  const increaseQuantity = () => {
    setQuantity(prev => Math.min(prev + 1, 10));
  };

  const decreaseQuantity = () => {
    setQuantity(prev => Math.max(prev - 1, 1));
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '48px', height: '48px' }} />
          <p style={{ marginTop: '16px', color: '#666', fontSize: '15px' }}>Loading combo details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !combo) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <FaTimesCircle size={48} color="#ef4444" style={{ marginBottom: '12px' }} />
          <p style={{ color: '#ef4444', fontSize: '16px', marginBottom: '20px' }}>{error || 'Combo not found'}</p>
          <button 
            className="btn btn-dark" 
            onClick={() => navigate('/shop')} 
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FaArrowLeft size={13} /> Back to Shop
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const discountPercentage = combo.discount_percentage || 
    ((combo.mrp - combo.selling_price) / combo.mrp * 100).toFixed(1);
  const savings = combo.mrp - combo.selling_price;
  const isInWishlist = wishlist.includes(combo?.id);

  return (
    <>
      <Navbar />

      {/* Warning Toast */}
      {showWarning && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px',
          backgroundColor: warningMessage.includes('added') || warningMessage.includes('removed') || warningMessage.includes('copied') ? '#4CAF50' : '#ff4444',
          color: 'white', padding: '15px 25px', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: '10px', animation: 'slideIn 0.3s ease'
        }}>
          <i className={`fa-${warningMessage.includes('added') || warningMessage.includes('removed') || warningMessage.includes('copied') ? 'regular fa-circle-check' : 'solid fa-circle-exclamation'}`}></i>
          <span>{warningMessage}</span>
          <button onClick={() => setShowWarning(false)} style={{ background: 'none', border: 'none', color: 'white', marginLeft: '10px', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <div className="login-popup-overlay" onClick={() => setShowLoginPopup(false)}>
          <div className="login-popup-container" onClick={(e) => e.stopPropagation()}>
            <button className="login-popup-close" onClick={() => setShowLoginPopup(false)}>
              <i className="fa-solid fa-times"></i>
            </button>
            <Login setOpen={setShowLoginPopup} onLoginSuccess={handleLoginSuccess} />
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Combo Details</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/"><i className="fa-solid fa-house"></i></a></li>
                    <li className="breadcrumb-item"><a href="/shop">Shop</a></li>
                    <li className="breadcrumb-item active">Combo Offer</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section className="product-section">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12">
              <div className="row g-4">

                {/* LEFT - Images */}
               <div className="col-xl-6">
  <div className="product-left-box">
    
    {/* MAIN IMAGE */}
    <div className="product-main-image mb-3">
      <img 
        src={getImage(selectedImage)} 
        className="img-fluid"
        alt={combo.name}
        style={{ width: '100%', borderRadius: '8px' }} 
      />
    </div>

    {/* THUMBNAILS */}
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      
      {/* Combo Main Image */}
      {combo.image_url && (
        <img
          src={getImage(combo.image_url)}
          onClick={() => setSelectedImage(combo.image_url)}
          style={{
            width: '70px',
            height: '70px',
            objectFit: 'cover',
            borderRadius: '6px',
            cursor: 'pointer',
            border: selectedImage === combo.image_url ? '2px solid #000' : '1px solid #ddd'
          }}
        />
      )}

      {/* Variant Images */}
      {combo.items && combo.items.map((item) => (
        item.variant_image && (
          <img
            key={item.id}
            src={getImage(item.variant_image)}
            onClick={() => setSelectedImage(item.variant_image)}
            style={{
              width: '70px',
              height: '70px',
              objectFit: 'cover',
              borderRadius: '6px',
              cursor: 'pointer',
              border: selectedImage === item.variant_image ? '2px solid #000' : '1px solid #ddd'
            }}
          />
        )
      ))}
    </div>

  </div>
</div>

                {/* RIGHT - Details */}
                <div className="col-xl-6">
                  <div className="right-box-contain">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="offer-top" style={{ background: '#ffffff', color: 'white', padding: '4px 12px', borderRadius: '20px', display: 'inline-block' }}>
                         
                        </h6>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        
                        <button
                          onClick={shareCombo}
                          style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}
                        >
                          <FaShareAlt />
                        </button>
                      </div>
                    </div>
                     <div style={{ marginBottom: '15px' }}>
  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
    {/* COMBO OFFER Badge */}
    <span style={{ background: '#000', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
      <FaLayerGroup style={{ marginRight: '4px' }} /> COMBO OFFER
    </span>
    
    {/* BESTSELLER Badge */}
    {combo?.is_bestseller === true && (
      <span style={{ background: '#ff9800', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
        ⭐ BESTSELLER
      </span>
    )}
    
    {/* MOST POPULAR Badge */}
    {combo?.is_most_popular === true && combo?.is_bestseller !== true && (
      <span style={{ background: '#9b59b6', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
        🔥 MOST POPULAR
      </span>
    )}
  </div>
  
  <h2 className="name" style={{ fontSize: '24px', fontWeight: '700', marginTop: '0' }}>
    {combo.name}
  </h2>
</div>
                    

                   <div className="price-rating">
  <h3
    className="theme-color price"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap'
    }}
  >

    {/* Selling Price */}
    <span
      style={{
        color: '#000',
        fontWeight: '700'
      }}
    >
      ₹{parseFloat(combo.selling_price).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}
    </span>

    {/* MRP Cross */}
    {combo.mrp > combo.selling_price && (
      <span
  style={{
    color: '#999',
    fontSize: '20px',
    fontWeight: '500',
    position: 'relative',
    display: 'inline-block'
  }}
>
  ₹{parseFloat(combo.mrp).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}

  {/* Custom Cross Line */}
  <span
    style={{
      position: 'absolute',
      left: '0',
      top: '50%',
      width: '100%',
      height: '1.5px',
      background: '#999',
      transform: 'translateY(-50%)'
    }}
  />
</span>
    )}

    {/* Discount */}
    {combo.mrp > combo.selling_price && (
      <span
        style={{
          color: '#388e3c',
          fontSize: '16px',
          fontWeight: '600'
        }}
      >
        ({Math.round(discountPercentage)}% OFF)
      </span>
    )}

  </h3>
</div>

                    {/* Savings Info */}
                    {savings > 0 && (
                      <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                        <span style={{ 
                          backgroundColor: '#e8f5e9', 
                          color: '#4caf50', 
                          padding: '6px 12px', 
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}>
                          <i className="fa-solid fa-tag" style={{ marginRight: '5px' }}></i>
                          Save ₹{savings.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
<div className="amazon-store-card">
  <div className="amazon-store-left">
    <p className="amazon-store-title">Available on</p>

    <img
      src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
      alt="Amazon"
      className="amazon-store-logo"
    />

    <p className="amazon-store-text">
      Shop with confidence &amp; enjoy fast delivery.
    </p>
  </div>

  <div className="amazon-store-middle">
    <div className="amazon-store-feature">
      <span>🟠</span>
      <span>Genuine Product</span>
    </div>

    <div className="amazon-store-feature">
      <span>🟠</span>
      <span>Fast Delivery</span>
    </div>

    <div className="amazon-store-feature">
      <span>🟠</span>
      <span>Easy Returns</span>
    </div>
  </div>
</div>
                    {/* Items in Combo */}
                    <div className="product-package mt-3">
                      <div className="product-title">
                        <h4>Items in this Combo ({combo.item_count || (combo.items ? combo.items.length : 0)} items)</h4>
                      </div>
                      <div style={{ 
                        maxHeight: '400px', 
                        overflowY: 'auto',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        padding: '10px'
                      }}>
                        {combo.items && combo.items.map((item, idx) => (
                          <div key={item.id} style={{ 
                            display: 'flex', 
                            gap: '12px', 
                            padding: '12px',
                            borderBottom: idx < combo.items.length - 1 ? '1px solid #f0f0f0' : 'none',
                            alignItems: 'center'
                          }}>
                          <div 
  style={{ 
    width: '60px', 
    height: '60px', 
    borderRadius: '8px', 
    overflow: 'hidden', 
    backgroundColor: '#f8f9fa', 
    flexShrink: 0,
    cursor: 'pointer',
    border: selectedImage === (item.variant_image || item.product_image) 
      ? '2px solid #000' 
      : '1px solid #eee'
  }}
  onClick={() => setSelectedImage(item.variant_image || item.product_image)}
>
  <img 
    src={getImage(item.variant_image || item.product_image)} 
    alt={item.product_name}
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
  />
</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                                {item.product_name}
                              </div>
                              {item.variant_name && (
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                                  {item.variant_name}
                                </div>
                              )}
                              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                Qty: {item.quantity}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {(!combo.items || combo.items.length === 0) && (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            <i className="fa-solid fa-box-open" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
                            Item details not available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quantity Selector */}
                    <div className="product-package mt-3">
                      <div className="product-title">
                        <h4>Quantity:</h4>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '5px', overflow: 'hidden' }}>
                          <button
                            onClick={decreaseQuantity}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: 'none',
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: '600'
                            }}
                          >
                            −
                          </button>
                          <span style={{ width: '50px', textAlign: 'center', fontSize: '16px', fontWeight: '600' }}>
                            {quantity}
                          </span>
                          <button
                            onClick={increaseQuantity}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: 'none',
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: '600'
                            }}
                          >
                            +
                          </button>
                        </div>
                        <span style={{ fontSize: '13px', color: '#666' }}>
                          Max 10 units
                        </span>
                      </div>
                    </div>

                    {/* Total Price */}
                    <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>Total Amount:</span>
                        <span style={{ fontSize: '22px', fontWeight: '700', color: '#000' }}>
                          ₹{(combo.selling_price * quantity).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {quantity > 1 && combo.mrp > combo.selling_price && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                          <span style={{ fontSize: '13px', color: '#666' }}>You save:</span>
                          <span style={{ fontSize: '13px', color: '#4caf50', fontWeight: '600' }}>
                            ₹{((combo.mrp - combo.selling_price) * quantity).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={addToCart}
                      disabled={addingToCart}
                      style={{
                        width: '100%',
                        padding: '14px',
                        backgroundColor: addingToCart ? '#666' : '#000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: addingToCart ? 'not-allowed' : 'pointer',
                        marginTop: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                    >
                      {addingToCart ? (
                        <><span className="spinner-border spinner-border-sm" role="status" /> Adding...</>
                      ) : (
                        <><FaShoppingCart /> Add to Cart</>
                      )}
                    </button>

                    <button
                      onClick={() => navigate('/cart')}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'transparent',
                        color: '#000',
                        border: '2px solid #000',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '10px'
                      }}
                    >
                      View Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      {combo.description && (
        <section className="collapsible-section">
          <div className="container">
            <div className="accordion-item active">
              <div className="accordion-header" style={{ padding: '18px 24px', background: '#fff', borderBottom: '1px solid #eee' }}>
                <span>Description</span>
              </div>
              <div className="accordion-content" style={{ padding: '20px 24px' }}>
                <p style={{ lineHeight: '1.6', color: '#555' }}>{combo.description}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Combo Benefits Section */}
      <section className="evercore-standard" style={{ marginTop: '40px', padding: '60px 0', background: '#f8f9fa' }}>
        <div className="container-fluid-lg">
          <div className="standard-container" style={{ textAlign: 'center' }}>
            <span className="standard-eyebrow" style={{ fontSize: '12px', letterSpacing: '2px', color: '#666', textTransform: 'uppercase' }}>COMBO BENEFITS</span>
            <h2 style={{ fontSize: '32px', fontWeight: '700', margin: '15px 0 40px', color: '#333' }}>Why Choose This Combo</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '30px', maxWidth: '1000px', margin: '0 auto' }}>
              {[
                ['mdi:tag-multiple', `Save ${Math.round(discountPercentage)}%`],
                ['mdi:package-variant', `${combo.item_count || (combo.items ? combo.items.length : 0)} Premium Products`],
                ['mdi:currency-inr', 'Best Value Deal'],
                ['mdi:star', 'Curated for Results'],
              ].map(([icon, label]) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '20px' }}>
                  <Icon icon={icon} style={{ width: '48px', height: '48px', color: '#000' }} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .breadcrumb-section {
          padding: 40px 0 20px;
          background: #f8f9fa;
        }
        .breadcrumb-contain {
          text-align: center;
        }
        .breadcrumb-contain h2 {
          font-size: 32px;
          font-weight: 600;
          margin-bottom: 10px;
        }
        .breadcrumb {
          display: flex;
          justify-content: center;
          gap: 10px;
          list-style: none;
          padding: 0;
        }
        .breadcrumb-item a {
          color: #666;
          text-decoration: none;
        }
        .breadcrumb-item.active {
          color: #000000;
        }
        .product-section {
          padding: 40px 0;
        }
        .container-fluid-lg {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 30px;
        }
        .product-left-box {
          background: #fff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
        }
        .right-box-contain {
          background: #fff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
        }
        .name {
          font-size: 24px;
          font-weight: 700;
          margin: 15px 0;
          color: #333;
        }
        .price {
          font-size: 28px;
          font-weight: 700;
          color: #000;
        }
        .product-title h4 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 12px;
        }
        .collapsible-section {
          padding: 40px 0;
          background: #fff;
        }
        .collapsible-section .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .accordion-item {
          border: 1px solid #eee;
          border-radius: 8px;
          margin-bottom: 15px;
          overflow: hidden;
        }

        /* Login Popup Styles */
        .login-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }

        .login-popup-container {
          position: relative;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          background: transparent;
          border-radius: 20px;
          animation: slideUp 0.3s ease;
        }

        .login-popup-close {
          position: absolute;
          top: 15px;
          right: 15px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          font-size: 16px;
          color: #333;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .login-popup-close:hover {
          background: #f0f0f0;
          transform: scale(1.1);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @media (max-width: 768px) {
          .container-fluid-lg {
            padding: 0 15px;
          }
          .right-box-contain {
            padding: 20px;
          }
          .name {
            font-size: 20px;
          }
          .price {
            font-size: 24px;
          }
        }
      `}</style>
    </>
  );
};

export default ComboDetail;